import type { Book, Recommendation } from "../../models/types";
import { STATUS_LABELS } from "../../models/types";
import { renderStarInput, esc } from "./stars";
import { renderCoverImage } from "./cover-image";
import { renderDateChipSelector } from "./date-chip-selector";

export function renderBookForm(book: Book, _isNew: boolean): string {
  return `
    <form class="book-form" data-book-id="${book.id}">
      <div class="book-form__field book-form__field--full book-form__cover-preview">
        <div id="cover-preview">${renderCoverImage(book.coverUrl, "large")}</div>
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-title">Title</label>
        <input class="lcars-input" id="bf-title" name="title" value="${esc(book.title)}" placeholder="Book title" required />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-author">Author</label>
        <input class="lcars-input" id="bf-author" name="author" value="${esc(book.author)}" placeholder="Author name" required />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-published">Date Published</label>
        <input class="lcars-input" id="bf-published" name="datePublished" value="${esc(book.datePublished)}" placeholder="e.g. 2024" />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-isbn">ISBN</label>
        <input class="lcars-input" id="bf-isbn" name="isbn" value="${esc(book.isbn)}" placeholder="ISBN number" />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-cover-url">Cover URL</label>
        <input class="lcars-input" id="bf-cover-url" name="coverUrl" value="${esc(book.coverUrl)}" placeholder="Cover image URL" />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-series">Series</label>
        <input class="lcars-input" id="bf-series" name="series" value="${esc(book.series)}" placeholder="Series name" />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-series-num">Book #</label>
        <input class="lcars-input" id="bf-series-num" name="seriesNumber" value="${esc(book.seriesNumber)}" placeholder="e.g. 1, 1.1, 2a" />
      </div>

      <div class="book-form__field">
        <label class="book-form__label" for="bf-status">Status</label>
        <select class="lcars-select" id="bf-status" name="status">
          ${Object.entries(STATUS_LABELS)
            .map(
              ([val, label]) =>
                `<option value="${val}" ${book.status === val ? "selected" : ""}>${label}</option>`,
            )
            .join("")}
        </select>
      </div>

      ${renderDateChipSelector({
        fieldName: "dateStarted",
        fieldId: "bf-date-started",
        label: "Date Started",
        currentValue: book.dateStarted,
      })}

      ${renderDateChipSelector({
        fieldName: "dateRead",
        fieldId: "bf-date-read",
        label: "Date Completed",
        currentValue: book.dateRead,
      })}

      <div class="book-form__field">
        <label class="book-form__label">Rating</label>
        ${renderStarInput(book.rating, "rating")}
      </div>

      <div class="book-form__field book-form__field--full">
        <label class="book-form__label" for="bf-notes">Notes</label>
        <textarea class="lcars-input" id="bf-notes" name="notes" placeholder="Personal notes...">${esc(book.notes)}</textarea>
      </div>

      <div class="book-form__field book-form__field--full">
        <label class="book-form__label">Recommendations</label>
        <div id="recommendations-list">
          ${renderRecommendations(book.recommendations)}
        </div>
        <div style="display:flex;gap:8px;margin-top:8px;align-items:end;flex-wrap:wrap;">
          <div style="display:flex;flex-direction:column;gap:4px;flex:1;min-width:120px;">
            <label class="book-form__label" for="rec-from">From</label>
            <input class="lcars-input" id="rec-from" placeholder="Who recommended it?" style="width:100%;" />
          </div>
          <div style="display:flex;flex-direction:column;gap:4px;flex:2;min-width:200px;">
            <label class="book-form__label" for="rec-comment">What they said</label>
            <input class="lcars-input" id="rec-comment" placeholder="What did they say?" style="width:100%;" />
          </div>
          <button type="button" class="lcars-btn lcars-btn--violet lcars-btn--small" id="add-rec-btn">Add</button>
        </div>
      </div>

    </form>
  `;
}

function renderRecommendations(recs: Recommendation[]): string {
  if (recs.length === 0)
    return '<div style="color:var(--lcars-tan);font-size:0.85rem;">No recommendations yet.</div>';

  return recs
    .map(
      (rec, i) => `
        <div class="recommendation">
          <div class="recommendation__from">${esc(rec.from)}</div>
          <div class="recommendation__comment">${esc(rec.comment)}</div>
          <button type="button" class="lcars-btn lcars-btn--red lcars-btn--small remove-rec-btn" data-rec-index="${i}" style="margin-top:6px;">Remove</button>
        </div>
      `,
    )
    .join("");
}
